import { TraceLink } from 'components/trace-link';
import { useTraceLink } from 'hooks/use-trace-link';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { FC, PropsWithChildren } from 'react';
import { FormIdProps } from 'shared/props';
import { getWorkspaceTabTraceId, getWorkspaceType, hasWorkspaceTabError } from 'state/workspaces/tabs/tabs.selector';
import { workspaceResetTabRequestedThunk } from 'state/workspaces/workspace.thunks';

import { ResetButton } from './common-form/components/ResetButton';
import { FatalError } from './FatalError/FatalError';

type ErrorFormProps = {
  onResetTabClicked: () => void;
  traceLink?: string;
};

const ErrorForm: FC<ErrorFormProps> = ({ onResetTabClicked, traceLink }) => {
  return (
    <FatalError titleKey="errorForm.title" detailsKey="errorForm.details">
      <div>
        <ResetButton className="btn-default mt-3" onResetClicked={onResetTabClicked} isEnabled />
      </div>
      {traceLink && (
        <div className="mt-3">
          <TraceLink href={traceLink} />
        </div>
      )}
    </FatalError>
  );
};
const useTabGuard = (formId: string) => {
  const hasFatalError = useAppSelector(s => hasWorkspaceTabError(s, formId));
  const traceId = useAppSelector(s => getWorkspaceTabTraceId(s, formId));
  const workspaceType = useAppSelector(s => getWorkspaceType(s, formId));
  const dispatch = useThunkDispatch();
  const resetTab = () => {
    if (workspaceType) {
      dispatch(workspaceResetTabRequestedThunk(formId, workspaceType));
    }
  };
  return {
    hasFatalError,
    resetTab,
    traceLink: useTraceLink(traceId),
  };
};
type Props = PropsWithChildren<FormIdProps>;
export function FormTabGuard({ formId, children }: Props) {
  const { hasFatalError, resetTab, traceLink } = useTabGuard(formId);

  if (hasFatalError) {
    return <ErrorForm onResetTabClicked={resetTab} traceLink={traceLink} />;
  }
  return children;
}
